import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, BookOpen, ShoppingCart, FileText, Star, Download, Search, ExternalLink } from 'lucide-react';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ebooks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');
  const [searchTerm, setSearchTerm] = useState('');
  const [addingToCart, setAddingToCart] = useState(null);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();

  // Update filter when URL changes
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    if (urlFilter && urlFilter !== filter) {
      setFilter(urlFilter);
    }
  }, [searchParams]);

  // Update URL when filter changes
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (newFilter === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ filter: newFilter });
    }
  };

  useEffect(() => {
    const fetchEbooks = async () => {
      try {
        const res = await axios.get(`${API}/ebooks`);
        setEbooks(res.data);
      } catch (err) {
        console.error('Failed to load e-books:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEbooks();
  }, []);

  const handleAddToCart = async (ebook) => {
    if (!isAuthenticated) {
      toast.error('Please log in to add items to cart');
      return;
    }
    setAddingToCart(ebook.id);
    try {
      await addToCart({
        product_id: ebook.id,
        ebook_id: ebook.id,
        name: ebook.name,
        price: ebook.price,
        image_url: ebook.cover_url,
        is_digital: true,
        quantity: 1,
      });
      toast.success(`"${ebook.name}" added to cart`);
    } catch (err) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(null);
    }
  };

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'ebook', label: 'E-books' },
    { value: 'guide', label: 'Guides' },
    { value: 'workbook', label: 'Workbooks' },
    { value: 'journal', label: 'Journals' },
  ];

  const filtered = ebooks.filter(eb => {
    const matchSearch = eb.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (eb.author || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (eb.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchCat = filter === 'all' || eb.category === filter;
    return matchSearch && matchCat;
  });

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="ebooks-page">
      {/* Hero */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 mb-3 px-4 py-1.5 bg-primary/10 rounded-full">
          <BookOpen className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-primary">Digital Library</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-foreground mb-3">
          E-books & Digital Guides
        </h1>
        <p className="text-muted-foreground text-base sm:text-lg max-w-2xl mx-auto">
          Instant downloads. Read anywhere. Explore our collection of curated e-books and digital guides.
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by title, author..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="ebooks-search"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {categories.map(cat => (
            <Button
              key={cat.value}
              variant={filter === cat.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterChange(cat.value)}
              data-testid={`filter-${cat.value}`}
            >
              {cat.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-20">
          <BookOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
          <h2 className="font-serif text-xl font-bold mb-2">
            {searchTerm || filter !== 'all' ? 'No results found' : 'Coming Soon'}
          </h2>
          <p className="text-muted-foreground">
            {searchTerm || filter !== 'all' ? 'Try a different search or filter' : 'Our digital library is being curated. Check back soon!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filtered.map(ebook => (
            <Card key={ebook.id} className="group overflow-hidden hover:shadow-lg transition-all" data-testid={`ebook-card-${ebook.id}`}>
              <Link to={`/ebooks/${ebook.id}`} className="block">
                <div className="relative aspect-[3/4] bg-gradient-to-b from-muted to-muted/50 flex items-center justify-center overflow-hidden">
                  {ebook.cover_url ? (
                    <img
                      src={ebook.cover_url}
                      alt={ebook.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-3 text-muted-foreground">
                      <BookOpen className="h-16 w-16" />
                      <span className="text-xs font-medium uppercase tracking-wider">{ebook.file_format?.toUpperCase()}</span>
                    </div>
                  )}
                  {ebook.is_featured && (
                    <Badge className="absolute top-3 left-3 bg-amber-500 text-white flex items-center gap-1 text-xs" data-testid="featured-badge">
                      <Star className="h-3 w-3 fill-current" />
                      Featured
                    </Badge>
                  )}
                  <Badge variant="outline" className="absolute top-3 right-3 bg-white/90 text-xs">
                    {ebook.file_format?.toUpperCase()}
                  </Badge>
                </div>
              </Link>
              <CardContent className="p-4">
                <Link to={`/ebooks/${ebook.id}`}>
                  <h3 className="font-serif font-bold text-foreground line-clamp-2 hover:text-primary transition-colors" data-testid="ebook-title">
                    {ebook.name}
                  </h3>
                </Link>
                {ebook.author && (
                  <p className="text-sm text-muted-foreground mt-1">by {ebook.author}</p>
                )}
                <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                  <FileText className="h-3 w-3" />
                  <span>{ebook.category}</span>
                  {ebook.pages > 0 && <span>- {ebook.pages} pages</span>}
                  {ebook.file_size > 0 && <span>- {formatSize(ebook.file_size)}</span>}
                </div>
                <div className="flex items-center justify-between mt-4">
                  <span className="font-bold text-lg text-primary">${ebook.price?.toFixed(2)}</span>
                  {ebook.affiliate_url ? (
                    <a href={ebook.affiliate_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                      <Button
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`affiliate-${ebook.id}`}
                      >
                        <ExternalLink className="h-4 w-4 mr-1" />
                        Buy
                      </Button>
                    </a>
                  ) : (
                    <Button
                      size="sm"
                      className="btn-accent"
                      onClick={(e) => { e.preventDefault(); handleAddToCart(ebook); }}
                      disabled={addingToCart === ebook.id}
                      data-testid={`add-to-cart-${ebook.id}`}
                    >
                      {addingToCart === ebook.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <ShoppingCart className="h-4 w-4 mr-1" />
                          Add to Cart
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Ebooks;
